<script>
  import { createEventDispatcher } from "svelte";
  import isEmpty from "lodash/isEmpty";
  import { objectify } from "overline";
  import { Dimension } from "dimtext";
  import { gt, lt } from "overline";
  import DimtextInput from "#lib/sidebar/DimtextInput.svelte";
  import BooleanInput from "#lib/sidebar/BooleanInput.svelte";
  import { selectedLocations } from "#src/stores/ui.js";
  import { gridlinePositions, createItem, createLocation } from "@local/lamina-core";
  import { multiplier } from "@local/extensions/utilities/scales.js";
  import { computeCellRectangles } from "@local/extensions/drawing/cell-rectangles.js";
  import { profile } from "#src/stores/auth.js";

  export let group;
  export let docid;
  export let items;
  export let collections;

  const dispatch = createEventDispatcher();

  $: displayUnit = $group?.data.settings?.display_unit || "inches";

  let selectedOnly = true;
  let inset =
    displayUnit === "millimeters" ? Dimension.from(10, "millimeters") : Dimension.from(0.5, "inches");
  let minimumDimension =
    displayUnit === "millimeters" ? Dimension.from(50, "millimeters") : Dimension.from(2.5, "inches");

  function createItems() {
    const selected = Object.keys($selectedLocations);

    const doc = $group.documents[docid];
    let did = doc?.type === "virtual" ? doc.source_document_id : docid;

    const allOpenings =
      selected.length && selectedOnly
        ? selected
            .map((id) => {
              const loc = $group.locations[id];
              if (loc?.record_type !== "collection" || loc?.document_id !== did) return null;
              if ($group.items[loc.record_id]?.drawing?.attachment_id !== id) return null;
              return $group.items[loc.record_id];
            })
            .filter((item) => item)
        : $group.items.order.filter((id) => $group.items[id]?.is_collection).map((id) => $group.items[id]);

    const openings = allOpenings.filter((item) => {
      const loc = $group.locations[item.drawing?.attachment_id];
      return loc && loc.document_id === did;
    });

    const locations = openings.map((item) => $group.locations[item.drawing.attachment_id]);

    const openingObj = objectify(openings, "id");
    const toDelete = $group.items.order.reduce((acc, id) => {
      const item = $group.items[id];
      if (item.is_collection) return acc;
      if (openingObj[item.collection_id]) {
        acc.push(id);
      }
      return acc;
    }, []);

    const tdObj = toDelete.reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
    const locsToDelete = $group.locations.order.reduce((acc, id) => {
      const loc = $group.locations[id];
      if (loc.record_type === "item" && tdObj[loc.record_id]) {
        acc.push(id);
      }
      return acc;
    }, []);

    const insetNum = inset.toNumber("inches");
    const minNum = minimumDimension.toNumber("inches");
    const newItems = [];
    const newLocations = [];
    openings.forEach((item, index) => {
      const loc = locations[index];
      let m = 1;
      let offset = { x: 0, y: 0 };
      if (loc.clip?.drawing_scale) {
        m = 300 / multiplier(loc.clip.drawing_scale);

        if (loc.clip?.drawing_offset) {
          offset = {
            x: -loc.clip.drawing_offset.x * m,
            y: -loc.clip.drawing_offset.y * m,
          };
        }
      }

      if (item.data.reference_planes?.type !== "grid") return;

      const hor = gridlinePositions(item.data.reference_planes, "horizontal", item.cache.bbox.ymin).filter(
        (pos) => gt(pos, item.cache.bbox.ymin) && lt(pos, item.cache.bbox.ymax),
      );
      hor.unshift(item.cache.bbox.ymin);
      hor.push(item.cache.bbox.ymax);
      const vert = gridlinePositions(item.data.reference_planes, "vertical", item.cache.bbox.xmin).filter(
        (pos) => gt(pos, item.cache.bbox.xmin) && lt(pos, item.cache.bbox.xmax),
      );
      vert.unshift(item.cache.bbox.xmin);
      vert.push(item.cache.bbox.xmax);

      let label = 1;
      const voids = item.data.reference_planes?.voids ?? [];
      const vObj = voids.reduce((acc, v) => {
        acc[`${v.row}_${v.col}`] = true;
        return acc;
      }, {});

      const rectangles = computeCellRectangles(hor, vert, item.cache.mergeStore);
      rectangles.forEach((r) => {
        const w = r.width - insetNum * 2;
        const h = r.height - insetNum * 2;
        const tooSmall = w < minNum || h < minNum;
        const isVoid = vObj[`${r.row}_${r.col}`];
        if (!tooSmall && !isVoid) {
          const width = Dimension.from(w, "inches");
          const height = Dimension.from(h, "inches");
          const child = createItem($group.id, $profile?.id, [], [], { width, height });
          child.mark = `${item.mark}.${label}`;
          child.collection_id = item.id;
          newItems.push(child);

          const position = {
            x: loc.clip.x + offset.x + (r.x + insetNum + w / 2) * m,
            y: loc.clip.y + loc.clip.height - (offset.y + (r.y + insetNum + h / 2) * m),
          };

          const newLoc = createLocation({
            group_id: $group.id,
            document_id: did,
            index: $group.locations.order.length + newLocations.length,
            position,
            record_id: child.id,
            record_type: "item",
            page_id: loc.page_id,
          });
          newLocations.push(newLoc);

          label += 1;
        }
      });
    });

    const updates = [];
    if (toDelete.length) {
      updates.push({ type: "item", action: "remove", ids: toDelete });
    }
    if (locsToDelete.length) {
      updates.push({ type: "location", action: "remove", ids: locsToDelete });
    }
    if (newItems.length) {
      updates.push({ type: "item", action: "add", records: newItems });
    }
    if (newLocations.length) {
      updates.push({ type: "location", action: "add", records: newLocations });
    }

    if (updates.length) {
      group.update(updates);
    }

    dispatch("close");
  }
</script>

<div class="space-y-2">
  <p>Create items within Openings</p>
  <DimtextInput labelWidth="8rem" border label="Inset" settings={$group?.data.settings} bind:value={inset} />
  <DimtextInput
    labelWidth="8rem"
    border
    label="Minimum size"
    settings={$group?.data.settings}
    bind:value={minimumDimension} />
  {#if !isEmpty($selectedLocations)}
    <BooleanInput labelWidth="8rem" border label="Selected markers only" bind:value={selectedOnly} />
  {/if}
  <div class="flex justify-end">
    <button class="btn btn-primary" on:click={createItems}>Create Items</button>
  </div>
</div>
